import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { environment } from '@env/environment';

/**
 * Marker used in APP_DATE_FORMATS: values formatted/parsed with it follow
 * `environment.dateFormat` (e.g. dd-MM-yyyy) instead of the locale default.
 */
const APP_DATE_INPUT_FORMAT = 'appDateInput';

/**
 * The same, plus the time of day — `app-datetime-picker` shows date and time in
 * ONE input (legacy `p-datePicker [showTime]`), so its text has to carry both.
 */
const APP_DATE_TIME_INPUT_FORMAT = 'appDateTimeInput';

const IS_12_HOURS = environment.timeFormat === '12h';

/**
 * Minutes, not seconds: `environment.dateTimeFormat` ends in `:ss` because it
 * DISPLAYS stored timestamps (tables, `appDateTime` pipe), while this is what an
 * operator types — and legacy's calendar with time offered minutes only.
 */
const DATE_TIME_INPUT_FORMAT = `${environment.dateFormat} ${IS_12_HOURS ? 'hh:mm a' : 'HH:mm'}`;

/**
 * Day-first date, optionally followed by the time of day. One pattern serves
 * both markers; the date-only field rejects a typed time (see `parseDayFirst`).
 */
const DATE_TIME_INPUT_PATTERN =
  /^(\d{1,2})[-./\s](\d{1,2})[-./\s](\d{4})(?:[\sT]+(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?)?$/i;

/**
 * Material date formats: date inputs use the app-wide `environment.dateFormat`,
 * time pickers follow `environment.timeFormat` (24h by default) regardless of
 * the browser locale.
 */
export const APP_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: APP_DATE_INPUT_FORMAT,
    timeInput: { hour: 'numeric', minute: 'numeric', hour12: IS_12_HOURS },
  },
  display: {
    dateInput: APP_DATE_INPUT_FORMAT,
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
    timeInput: { hour: '2-digit', minute: '2-digit', hour12: IS_12_HOURS },
    timeOptionLabel: { hour: '2-digit', minute: '2-digit', hour12: IS_12_HOURS },
  },
};

/**
 * Formats for a field whose text carries the time as well. Provided LOCALLY by
 * `app-datetime-picker`, never globally — the app-wide token has to keep saying
 * "date only", or every other calendar would start printing a time nobody set.
 */
export const APP_DATETIME_FORMATS: MatDateFormats = {
  parse: { ...APP_DATE_FORMATS.parse, dateInput: APP_DATE_TIME_INPUT_FORMAT },
  display: { ...APP_DATE_FORMATS.display, dateInput: APP_DATE_TIME_INPUT_FORMAT },
};

/** Midnight — what a date with no typed time means. */
const START_OF_DAY = { hours: 0, minutes: 0 };

interface TimeOfDay {
  hours: number;
  minutes: number;
}

/**
 * NativeDateAdapter that renders and parses date-input values using
 * `environment.dateFormat` (day-first, e.g. 06-07-2026). The locale (set by
 * LanguageService) still drives month/weekday names in the calendar.
 */
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: unknown): string {
    if (displayFormat === APP_DATE_INPUT_FORMAT) {
      return formatDate(date, environment.dateFormat, 'en-US');
    }
    if (displayFormat === APP_DATE_TIME_INPUT_FORMAT) {
      return formatDate(date, DATE_TIME_INPUT_FORMAT, 'en-US');
    }
    return super.format(date, displayFormat as Intl.DateTimeFormatOptions);
  }

  override parse(value: unknown, parseFormat?: unknown): Date | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      return this.parseDayFirst(trimmed, parseFormat === APP_DATE_TIME_INPUT_FORMAT);
    }
    return super.parse(value, parseFormat);
  }

  /**
   * Day-first input matching `environment.dateFormat`. A typed time is accepted
   * only by the date+time field: on a date-only field it stays invalid, exactly
   * as it was before that field existed.
   */
  private parseDayFirst(value: string, isTimeAllowed: boolean): Date | null {
    const match = DATE_TIME_INPUT_PATTERN.exec(value);
    if (!match) {
      return this.invalid();
    }

    const [, rawDay, rawMonth, rawYear, rawHours, rawMinutes, meridiem] = match;
    if (rawHours !== undefined && !isTimeAllowed) {
      return this.invalid();
    }

    const time = toTimeOfDay(rawHours, rawMinutes, meridiem);
    if (!time) {
      return this.invalid();
    }

    const day = Number(rawDay);
    const month = Number(rawMonth) - 1;
    const date = new Date(Number(rawYear), month, day, time.hours, time.minutes);
    return date.getDate() === day && date.getMonth() === month ? date : this.invalid();
  }
}

/** Null when the typed time is out of range; midnight when none was typed. */
function toTimeOfDay(
  rawHours: string | undefined,
  rawMinutes: string | undefined,
  meridiem: string | undefined,
): TimeOfDay | null {
  if (rawHours === undefined || rawMinutes === undefined) {
    return START_OF_DAY;
  }

  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (minutes > 59) {
    return null;
  }

  if (!meridiem) {
    return hours > 23 ? null : { hours, minutes };
  }

  if (hours < 1 || hours > 12) {
    return null;
  }
  const isAfternoon = meridiem.toLowerCase().startsWith('p');
  return { hours: (hours % 12) + (isAfternoon ? 12 : 0), minutes };
}
